/**
 * MKS-464 — the "Delivery" tab of the Manager overview: what is going on across projects and who
 * is doing, and not doing, the work, for Today / Yesterday / Last 7 / 14 / 30 days (Europe/Prague days).
 *
 * URL state (P-15): `view=people|projects` (default people) and `period=today|yesterday|7d|14d|30d`
 * (default today). Writing a default removes the param, an invalid value reads as the default,
 * and every update keeps the other params (notably `tab=delivery`).
 *
 * MKS-520 — the People table gets the Trend column. The weekly trend is fetched only with the
 * People view shown; a failed trend request only leaves dashes in the column, never the tab's
 * error state.
 */
import { useSearchParams } from 'react-router-dom';
import { Btn } from '../ui/Btn';
import { DeliveryPeopleTable } from './DeliveryPeopleTable';
import { DeliveryProjectsTable } from './DeliveryProjectsTable';
import { useDeliveryOverview } from '../../hooks/useDeliveryOverview';
import { useDeliveryWeeklyTrend } from '../../hooks/useDeliveryWeeklyTrend';
import type { DeliveryPeriodKey } from '../../services/api/delivery';
import { formatDisplayDate } from '../../utils/format';

type DeliveryView = 'people' | 'projects';

const PERIODS: Array<{ value: DeliveryPeriodKey; label: string }> = [
  { value: 'today', label: 'Today' },
  { value: 'yesterday', label: 'Yesterday' },
  { value: '7d', label: 'Last 7 days' },
  { value: '14d', label: 'Last 14 days' },
  { value: '30d', label: 'Last 30 days' },
];

const VIEWS: Array<{ value: DeliveryView; label: string }> = [
  { value: 'people', label: 'People' },
  { value: 'projects', label: 'Projects' },
];

const SEGMENT_BOX = 'inline-flex bg-ms-surface-2 rounded-md p-0.5 border border-ms-border-subtle';

function segmentClass(active: boolean): string {
  const tone = active
    ? 'bg-ms-surface text-ms-text-strong shadow-sm'
    : 'text-ms-text-secondary hover:text-ms-text-strong';
  return `px-2 py-0.5 rounded text-[11px] font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-ms-orange/40 ${tone}`;
}

export function DeliveryTab() {
  const [searchParams, setSearchParams] = useSearchParams();

  const periodParam = searchParams.get('period');
  const period: DeliveryPeriodKey =
    PERIODS.find((p) => p.value === periodParam)?.value ?? 'today';
  const view: DeliveryView = searchParams.get('view') === 'projects' ? 'projects' : 'people';

  function setParam(key: 'view' | 'period', value: string, defaultValue: string) {
    const next = new URLSearchParams(searchParams);
    if (value === defaultValue) next.delete(key);
    else next.set(key, value);
    setSearchParams(next, { replace: true });
  }

  const { data, isLoading, isError, refetch } = useDeliveryOverview(period);
  const trendQuery = useDeliveryWeeklyTrend(view === 'people');

  const label = data?.period.label ?? PERIODS.find((p) => p.value === period)?.label ?? 'Today';
  const range = data
    ? data.period.fromDay === data.period.toDay
      ? formatDisplayDate(data.period.fromDay)
      : `${formatDisplayDate(data.period.fromDay)} – ${formatDisplayDate(data.period.toDay)}`
    : '';

  return (
    <>
      <header className="px-7 pt-6 pb-5 border-b border-ms-border-subtle">
        <div className="flex items-end justify-between gap-6 flex-wrap">
          <div>
            <div className="text-[10px] uppercase tracking-wider font-medium text-ms-text-muted mb-1">
              {label}
              {range ? ` · ${range}` : ''}
            </div>
            <h1 className="text-[26px] font-semibold tracking-tight text-ms-text-strong">
              Delivery
            </h1>
          </div>
          <div className="flex items-center gap-4 flex-wrap">
            <div className={SEGMENT_BOX} role="group" aria-label="Period">
              {PERIODS.map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  className={segmentClass(opt.value === period)}
                  aria-pressed={opt.value === period}
                  onClick={() => setParam('period', opt.value, 'today')}
                >
                  {opt.label}
                </button>
              ))}
            </div>
            <div className={SEGMENT_BOX} role="tablist" aria-label="Delivery view">
              {VIEWS.map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  role="tab"
                  className={segmentClass(opt.value === view)}
                  aria-selected={opt.value === view}
                  onClick={() => setParam('view', opt.value, 'people')}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      </header>

      {isLoading && (
        <div className="px-7 py-5 flex-1">
          <div
            className="bg-ms-surface border border-ms-border-subtle rounded-lg overflow-hidden"
            aria-hidden="true"
            data-testid="delivery-skeleton"
          >
            {Array.from({ length: 8 }).map((_, i) => (
              <div
                key={i}
                className="h-[44px] border-b border-ms-border-subtle animate-pulse bg-ms-surface-2/30"
              />
            ))}
          </div>
        </div>
      )}

      {isError && !isLoading && (
        <div className="px-7 py-12 flex-1 flex items-start justify-center">
          <div className="bg-ms-surface border border-ms-border-subtle rounded-lg p-6 max-w-md w-full text-center">
            <div className="text-[12.5px] text-rose-600 mb-3">
              Couldn&apos;t load delivery data. Try again.
            </div>
            <Btn variant="outline-accent" size="sm" type="button" onClick={() => refetch()}>
              Retry
            </Btn>
          </div>
        </div>
      )}

      {data && !isLoading && !isError && (
        <div className="px-7 py-5 flex-1 overflow-auto">
          {view === 'people' ? (
            <DeliveryPeopleTable
              people={data.people}
              bots={data.bots}
              trend={trendQuery.data}
            />
          ) : (
            <DeliveryProjectsTable projects={data.projects} />
          )}
        </div>
      )}
    </>
  );
}

export default DeliveryTab;
